/**
 * Text recognition on the phone itself (Tesseract, Dutch model), for receipts, nutrition labels and cards.
 * Free and private: the engine and language data come from our own site (/ocr, cached after the first
 * use) and the photo never leaves the device for this step.
 */
import type { Worker as OcrWorker } from "tesseract.js";
import { reportClientError } from "../components/ErrorBoundary";
import { readingSize, thresholdPixels, type PrepOpts } from "./textPrep";

/** Where the engine is: shown to the person, and reported when it gets stuck. */
export type OcrStage = "loading" | "starting" | "reading";

let worker: Promise<OcrWorker> | null = null;
let onProgress: ((p: number, stage: OcrStage) => void) | null = null;
let lastStatus = "";

const STARTUP_MS = 25_000;
const READ_MS = 40_000;

function withTimeout<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(`ocr_timeout:${what}:${lastStatus}`)), ms);
    p.then(
      (v) => {
        window.clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        window.clearTimeout(timer);
        reject(e);
      },
    );
  });
}

function getWorker() {
  worker ??= withTimeout(
    import("tesseract.js").then(({ createWorker }) =>
      createWorker("nld", 1, {
        workerPath: "/ocr/worker.min.js",
        corePath: "/ocr/",
        langPath: "/ocr/lang",
        gzip: true,
        logger: (m: { status: string; progress: number }) => {
          lastStatus = `${m.status} ${Math.round(m.progress * 100)}%`;
          if (m.status === "recognizing text") onProgress?.(m.progress, "reading");
          else if (/loading/.test(m.status)) onProgress?.(m.progress, "loading");
          else onProgress?.(m.progress, "starting");
        },
        errorHandler: (e: unknown) => reportClientError(`ocr worker: ${String(e)}`),
      }),
    ),
    STARTUP_MS,
    "startup",
  );
  // A failed start (offline, blocked, too slow) shouldn't stay cached: the next try starts afresh.
  worker.catch((e) => {
    reportClientError(String(e?.message ?? e));
    worker = null;
  });
  return worker;
}

/**
 * Read the text in an image (a URL of our own, or a Blob). `progress` gets 0…1 and the stage while working.
 * Gives up (rejects) instead of hanging when the engine can't start or a read takes far too long.
 */
export async function readText(
  image: string | Blob,
  progress?: (p: number, stage?: OcrStage) => void,
  prep?: Parameters<typeof prepareForText>[1],
): Promise<string> {
  const mine = progress ?? null;
  onProgress = mine;
  try {
    // Already being read since the photo was taken (while it uploaded): just wait for that.
    const started = typeof image === "string" ? early.get(image) : undefined;
    if (started) {
      early.delete(image as string);
      return await started;
    }
    const blob = typeof image === "string" ? await fetch(image, { credentials: "same-origin" }).then((r) => r.blob()) : image;
    return await recognise(blob, prep);
  } finally {
    // Only clear our own: a newer read (a retaken photo) keeps reporting to its screen.
    if (onProgress === mine) onProgress = null;
  }
}

/** Photo URL → a read that started on the phone's own copy before the upload finished. */
const early = new Map<string, Promise<string>>();

/**
 * Start reading a photo right away, from the phone's own copy, while it is still uploading. Once the upload
 * is done, `linkEarlyRead(photoUrl(id), read)` lets the screen's `readText(photoUrl(id))` pick it up: no
 * waiting for the upload first, and no downloading the same photo back.
 */
export function startEarlyRead(blob: Blob): Promise<string> {
  const p = recognise(blob);
  p.catch(() => {});
  return p;
}

export function linkEarlyRead(url: string, read: Promise<string>) {
  early.set(url, read);
  // Never picked up (the screen didn't need the text): forget it.
  window.setTimeout(() => early.delete(url), 120_000);
}

async function recognise(blob: Blob, prep?: Parameters<typeof prepareForText>[1]): Promise<string> {
  try {
    // Every photo is cleaned up first: white-on-dark labels and receipts read as well as black-on-white.
    const ready = await prepareForText(blob, prep).catch(() => blob);
    const w = await getWorker();
    const { data } = await withTimeout(w.recognize(ready), READ_MS, "read");
    return data.text;
  } catch (e) {
    // A stuck engine is dropped so the next read starts a fresh one.
    if (String((e as Error)?.message).startsWith("ocr_timeout:read")) {
      reportClientError(String((e as Error).message));
      worker?.then((w) => w.terminate()).catch(() => {});
      worker = null;
    }
    throw e;
  }
}

/** Start the reader in the background (e.g. when a scanner opens), so the first read is quicker. */
export function warmUpOcr() {
  getWorker().catch(() => {});
}

/**
 * Progress for the screen: the reading percentage while reading; while the engine is still being fetched
 * or started, -1 (the screen then says it is getting ready instead of showing a frozen 0%).
 */
export const asReadingProgress = (set: (v: number) => void) => (p: number, stage?: OcrStage) => set(stage === "reading" || stage === undefined ? p : -1);

/**
 * A copy of a photo made for reading its text (see textPrep.ts). Done in a background worker when the
 * phone can, so the screen doesn't stutter; otherwise here.
 */
export async function prepareForText(file: Blob, opts: PrepOpts = {}): Promise<Blob> {
  if (!prepBroken && typeof OffscreenCanvas !== "undefined" && typeof Worker !== "undefined") {
    const done = await inWorker(file, opts).catch(() => null);
    if (done) return done;
  }
  const bitmap = await createImageBitmap(file);
  const { w, h } = readingSize(bitmap.width, bitmap.height, opts.maxSide);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  const img = ctx.getImageData(0, 0, w, h);
  thresholdPixels(img.data, w, h, opts);
  ctx.putImageData(img, 0, 0);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b ?? file), "image/png"));
}

let prepWorker: Worker | null = null;
/** The background worker couldn't run on this phone: from then on the clean-up is done on the page. */
let prepBroken = false;
let prepSeq = 0;
const prepWaiting = new Map<number, (b: Blob | null) => void>();

function inWorker(blob: Blob, opts: PrepOpts): Promise<Blob | null> {
  if (!prepWorker) {
    prepWorker = new Worker(new URL("./textPrep.worker.ts", import.meta.url), { type: "module" });
    prepWorker.onmessage = (e: MessageEvent<{ id: number; blob?: Blob; error?: string }>) => {
      prepWaiting.get(e.data.id)?.(e.data.blob ?? null);
      prepWaiting.delete(e.data.id);
    };
    prepWorker.onerror = () => {
      prepBroken = true;
      prepWorker?.terminate();
      prepWorker = null;
      // The worker can't run here: everything waiting is done on the page instead, and so is what follows.
      for (const done of prepWaiting.values()) done(null);
      prepWaiting.clear();
    };
  }
  const id = ++prepSeq;
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      // No answer at all: the worker is stuck here, so don't wait for it again.
      prepBroken = true;
      prepWaiting.delete(id);
      resolve(null);
    }, 15_000);
    prepWaiting.set(id, (b) => {
      window.clearTimeout(timer);
      resolve(b);
    });
    prepWorker!.postMessage({ id, blob, opts });
  });
}
